import { useCallback, useState } from "react";

/** 3 カラムの識別子. list=返信リスト / thread=メールスレッド / composer=メール返信フォーム */
export type ColumnKey = "list" | "thread" | "composer";

export type ColumnVisibility = Record<ColumnKey, boolean>;

/** 表示中のサイドカラム. list=返信リスト / composer=返信フォーム (両者は排他) */
export type ReplyPaneSide = "list" | "composer";

export type UseColumnVisibilityResult = {
  /** 各カラムの表示状態. thread は常時 true、list と composer は排他 */
  visibility: ColumnVisibility;
  /** 現在表示中のサイド (list=ブラウズ / composer=返信) */
  side: ReplyPaneSide;
  /** 返信リストを表示する (返信フォームを畳む) */
  showList: () => void;
  /** 返信フォームを表示する (返信リストを畳む) */
  showComposer: () => void;
  /** list ⇄ composer を反転する */
  toggleSide: () => void;
};

/**
 * 返信管理レイアウトの表示状態を管理する hook.
 * メールスレッドは常時表示し、返信リストと返信フォームは排他に切り替える
 * (常に最大 2 ペイン構成). 初期状態は「返信リスト + メールスレッド」.
 * @return {UseColumnVisibilityResult} 表示状態と操作関数
 */
export function useColumnVisibility(): UseColumnVisibilityResult {
  const [side, setSide] = useState<ReplyPaneSide>("list");

  const showList = useCallback(() => setSide("list"), []);
  const showComposer = useCallback(() => setSide("composer"), []);
  const toggleSide = useCallback(
    () => setSide(prev => (prev === "list" ? "composer" : "list")),
    [],
  );

  const visibility: ColumnVisibility = {
    list: side === "list",
    thread: true,
    composer: side === "composer",
  };

  return { visibility, side, showList, showComposer, toggleSide };
}
